"use client";

import {
  useRef,
  useEffect,
  useMemo,
  useState,
  forwardRef,
  useImperativeHandle,
} from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

// const EARTH_ROTATION_SPEED = (2 * Math.PI) / (24 * 60 * 60 * 1000);

// URLs de las texturas optimizadas del globo
const TEXTURE_URLS = [
  "./assets/map/Albedo_opt.jpg",
  "./assets/map/Bump_opt.jpg",
  "./assets/map/Ocean_opt.webp",
  "./assets/map/night_lights_opt.webp",
  "./assets/map/Clouds_opt.webp",
];

interface EarthTextures {
  albedoMap: THREE.Texture;
  bumpMap: THREE.Texture;
  oceanMap: THREE.Texture;
  lightsMap: THREE.Texture;
  cloudsMap: THREE.Texture;
}

/**
 * Carga una textura decodificando la imagen fuera del hilo principal
 * usando fetch + createImageBitmap, evitando bloquear la UI.
 */
async function loadTextureOffThread(url: string): Promise<THREE.Texture> {
  const res = await fetch(url);
  const blob = await res.blob();
  // imageOrientation "flipY" pone la imagen en orientación WebGL (bottom-left)
  const bitmap = await createImageBitmap(blob, {
    premultiplyAlpha: "none",
    colorSpaceConversion: "none",
    imageOrientation: "flipY",
  });
  const tex = new THREE.CanvasTexture(bitmap);
  // ImageBitmap ya está en orientación WebGL, evitar doble flip
  tex.flipY = false;
  tex.needsUpdate = true;
  return tex;
}

interface EarthSphereProps {
  rotationSpeed: number;
  speedFactor: number;
  onTexturesLoaded?: (
    _cloudsMap: THREE.Texture,
    _initialRotation: number
  ) => void;
}

export interface EarthSphereRef {
  getEarthRotation: () => number;
  getInitialRotation: () => number;
}

const EarthSphere = forwardRef<EarthSphereRef, EarthSphereProps>(
  ({ rotationSpeed, speedFactor, onTexturesLoaded }, ref) => {
    const meshRef = useRef<THREE.Mesh>(null!);
    const matRef = useRef<THREE.MeshStandardMaterial>(null);

    // Texturas cargadas de forma asíncrona para no bloquear el hilo principal
    const [textures, setTextures] = useState<EarthTextures | null>(null);

    // Cargar texturas fuera del hilo principal con createImageBitmap
    useEffect(() => {
      let cancelled = false;

      Promise.all(TEXTURE_URLS.map(loadTextureOffThread)).then((loaded) => {
        if (cancelled) return;
        setTextures({
          albedoMap: loaded[0],
          bumpMap: loaded[1],
          oceanMap: loaded[2],
          lightsMap: loaded[3],
          cloudsMap: loaded[4],
        });
      });

      return () => {
        cancelled = true;
      };
    }, []);

    // Calcular rotación inicial basada en hora UTC
    const { initialRotation, initialUvOffset } = useMemo(() => {
      const now = new Date();
      const utcHours =
        now.getUTCHours() +
        now.getUTCMinutes() / 60 +
        now.getUTCSeconds() / 3600;
      const rotation = (utcHours - 12) * (Math.PI / 12) - Math.PI / 2;
      const uvOffset = (rotation / (2 * Math.PI)) % 1;
      return { initialRotation: rotation, initialUvOffset: uvOffset };
    }, []);

    // Configurar propiedades de texturas después de cargar
    useEffect(() => {
      if (!textures) return;
      textures.albedoMap.colorSpace = THREE.SRGBColorSpace;
      textures.cloudsMap.wrapS = THREE.RepeatWrapping;

      // Notificar al padre que las texturas están cargadas
      onTexturesLoaded?.(textures.cloudsMap, initialRotation);
    }, [textures, initialRotation, onTexturesLoaded]);

    // Inyectar modificaciones de shader personalizadas para sombras de nubes y luces nocturnas
    useEffect(() => {
      if (!textures) return;
      const material = matRef.current;
      if (!material) return;

      material.onBeforeCompile = (shader) => {
        shader.uniforms.tClouds = { value: textures.cloudsMap };
        shader.uniforms.uv_xOffset = { value: initialUvOffset };

        shader.fragmentShader = shader.fragmentShader.replace(
          "#include <common>",
          `
            #include <common>
            uniform sampler2D tClouds;
            uniform float uv_xOffset;
          `
        );
        shader.fragmentShader = shader.fragmentShader.replace(
          "#include <roughnessmap_fragment>",
          `
            float roughnessFactor = roughness;
            #ifdef USE_ROUGHNESSMAP
              vec4 texelRoughness = texture2D( roughnessMap, vRoughnessMapUv );
              texelRoughness = vec4(1.0) - texelRoughness;
              roughnessFactor *= clamp(texelRoughness.g, 0.5, 1.0);
            #endif
          `
        );
        shader.fragmentShader = shader.fragmentShader.replace(
          "#include <emissivemap_fragment>",
          `
            #include <emissivemap_fragment>
            #ifdef USE_EMISSIVEMAP
              float nightFactor = 1.0 - smoothstep(-0.02, 0.0, dot(vNormal, directionalLights[0].direction));
              totalEmissiveRadiance *= nightFactor;
            #endif
            float cloudsMapValue = texture2D(tClouds, vec2(vMapUv.x - uv_xOffset, vMapUv.y)).r;
            diffuseColor.rgb *= max(1.0 - cloudsMapValue, 0.2);
            float atmosIntensity = 1.4 - dot(vNormal, vec3(0.0, 0.0, 1.0));
            vec3 atmosColor = vec3(0.3, 0.6, 1.0) * pow(atmosIntensity, 5.0);
            diffuseColor.rgb += atmosColor;
          `
        );
        material.userData.shader = shader;
      };

      // Forzar recompilación del material
      material.needsUpdate = true;
    }, [textures, initialUvOffset]);

    // Exponer handle imperativo para que el padre acceda a los valores de rotación
    useImperativeHandle(ref, () => ({
      getEarthRotation: () => meshRef.current?.rotation.y ?? 0,
      getInitialRotation: () => initialRotation,
    }));

    // Bucle de animación - rotar malla y actualizar offset UV del shader
    useFrame((_state, delta) => {
      if (!meshRef.current) return;

      const earthRotation = delta * 1000 * rotationSpeed * speedFactor;
      meshRef.current.rotation.y += earthRotation;

      // Actualizar offset UV de sombra de nubes en el shader
      const material = matRef.current;
      if (material?.userData.shader) {
        const shader = material.userData.shader as {
          uniforms: { uv_xOffset: { value: number } };
        };
        shader.uniforms.uv_xOffset.value += (earthRotation / (2 * Math.PI)) % 1;
      }
    });

    // No renderizar la malla hasta que las texturas estén listas
    if (!textures) return null;

    return (
      <mesh ref={meshRef} rotation-y={initialRotation}>
        <sphereGeometry args={[10, 64, 64]} />
        <meshStandardMaterial
          ref={matRef}
          map={textures.albedoMap}
          bumpMap={textures.bumpMap}
          bumpScale={0.03}
          roughnessMap={textures.oceanMap}
          metalness={0.1}
          metalnessMap={textures.oceanMap}
          emissiveMap={textures.lightsMap}
          emissive={new THREE.Color(0xffff88)}
        />
      </mesh>
    );
  }
);

EarthSphere.displayName = "EarthSphere";

export default EarthSphere;
